import { ActionMenu } from './Table/ActionMenu'
import { ActionMenuScope } from './Table/ActionMenuContext'
import { useEffect, useState } from 'react'
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import KeyIcon from '@mui/icons-material/Key'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { passkeysSupported } from '../passkeys'
import { isReauthenticationRequired, ReauthenticationForm } from './ReauthenticationForm'

export interface PasskeyItem {
    id: string
    name: string
    createdAt: Date
    lastUsedAt?: Date
}

interface PasskeyRegistrationStart {
    ceremonyId: string
    options: PublicKeyCredentialCreationOptionsJSON
}

export interface PasskeyApi {
    list: () => Promise<PasskeyItem[]>
    beginRegistration: () => Promise<PasskeyRegistrationStart>
    completeRegistration: (body: {
        ceremonyId: string
        name: string
        response: Awaited<ReturnType<typeof startRegistration>>
    }) => Promise<PasskeyItem>
    rename: (id: string, name: string) => Promise<PasskeyItem>
    delete: (id: string) => Promise<void>
    reauthenticate: (password: string) => Promise<void>
}

export interface PasskeyManagerProps {
    api: PasskeyApi
    promptEnrollment?: boolean
}

const formatDate = (date: Date) => new Date(date).toLocaleString()

export const PasskeyManager = ({ api, promptEnrollment = false }: PasskeyManagerProps) => {
    const supported = passkeysSupported()
    const [passkeys, setPasskeys] = useState<PasskeyItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>()
    const [enrollmentOpen, setEnrollmentOpen] = useState(promptEnrollment)
    const [enrollmentPrompt, setEnrollmentPrompt] = useState(promptEnrollment)
    const [name, setName] = useState('My passkey')
    const [renaming, setRenaming] = useState<PasskeyItem>()
    const [deleting, setDeleting] = useState<PasskeyItem>()
    const [submitting, setSubmitting] = useState(false)
    const [reauthenticationAction, setReauthenticationAction] = useState<'enroll' | 'remove'>()
    const [reauthenticating, setReauthenticating] = useState(false)

    useEffect(() => {
        let active = true
        api.list()
            .then((items) => {
                if (active) setPasskeys(items)
            })
            .catch(() => {
                if (active) setError('Passkeys could not be loaded.')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [api])

    const closeEnrollment = () => {
        if (submitting) return
        setEnrollmentOpen(false)
        setEnrollmentPrompt(false)
        setError(undefined)
    }

    const enroll = async () => {
        if (!supported || !name.trim() || submitting) return
        setSubmitting(true)
        setError(undefined)
        try {
            const { ceremonyId, options } = await api.beginRegistration()
            const response = await startRegistration({ optionsJSON: options })
            const passkey = await api.completeRegistration({ ceremonyId, name: name.trim(), response })
            setPasskeys((items) => [...items, passkey])
            setEnrollmentOpen(false)
            setEnrollmentPrompt(false)
            setName('My passkey')
        } catch (failure) {
            if (isReauthenticationRequired(failure)) {
                setReauthenticationAction('enroll')
            } else {
                setError('The passkey was not added. You can try again or use another device.')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const rename = async () => {
        if (!renaming || !name.trim() || submitting) return
        setSubmitting(true)
        setError(undefined)
        try {
            const updated = await api.rename(renaming.id, name.trim())
            setPasskeys((items) => items.map((item) => (item.id === updated.id ? updated : item)))
            setRenaming(undefined)
            setName('My passkey')
        } catch {
            setError('The passkey could not be renamed.')
        } finally {
            setSubmitting(false)
        }
    }

    const remove = async () => {
        if (!deleting || submitting) return
        setSubmitting(true)
        setError(undefined)
        try {
            await api.delete(deleting.id)
            setPasskeys((items) => items.filter((item) => item.id !== deleting.id))
            setDeleting(undefined)
        } catch (failure) {
            if (isReauthenticationRequired(failure)) {
                setReauthenticationAction('remove')
            } else {
                setError('The passkey could not be removed.')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const dialogError = error && (enrollmentOpen || renaming || deleting)

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h2">Passkeys</Typography>
                    <Typography color="text.secondary">
                        Use your fingerprint, face, screen lock, or security key to sign in.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={!supported}
                    onClick={() => {
                        setName('My passkey')
                        setEnrollmentPrompt(false)
                        setEnrollmentOpen(true)
                    }}
                >
                    Add passkey
                </Button>
            </Stack>

            {!supported && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    This browser does not support passkeys. You can still rename or remove existing passkeys.
                </Alert>
            )}

            {error && !dialogError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : passkeys.length === 0 ? (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No passkeys have been added yet.
                </Typography>
            ) : (
                <List disablePadding sx={{ mt: 1 }}>
                    {passkeys.map((passkey) => {
                        const rename = () => {
                            setName(passkey.name)
                            setRenaming(passkey)
                        }
                        const remove = () => setDeleting(passkey)
                        return (
                            <ActionMenuScope key={passkey.id}>
                                {(targetProps) => (
                                    <ListItem
                                        {...targetProps}
                                        divider
                                        secondaryAction={
                                            <ActionMenu
                                                options={[
                                                    { label: 'Rename passkey', icon: <EditIcon />, onClick: rename },
                                                    {
                                                        label: 'Remove passkey',
                                                        icon: <DeleteIcon />,
                                                        onClick: remove,
                                                        isDestructive: true,
                                                    },
                                                ]}
                                            >
                                                {' '}
                                                <Stack direction="row">
                                                    <Tooltip title="Rename passkey">
                                                        <IconButton
                                                            aria-label={`Rename ${passkey.name}`}
                                                            onClick={rename}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove passkey">
                                                        <IconButton
                                                            aria-label={`Remove ${passkey.name}`}
                                                            onClick={remove}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </ActionMenu>
                                        }
                                    >
                                        <ListItemIcon>
                                            <KeyIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={passkey.name}
                                            secondary={`Added ${formatDate(passkey.createdAt)}${
                                                passkey.lastUsedAt
                                                    ? ` · Last used ${formatDate(passkey.lastUsedAt)}`
                                                    : ''
                                            }`}
                                        />
                                    </ListItem>
                                )}
                            </ActionMenuScope>
                        )
                    })}
                </List>
            )}

            <Dialog open={enrollmentOpen} onClose={closeEnrollment} aria-labelledby="passkey-enrollment-title">
                <DialogTitle id="passkey-enrollment-title">
                    {enrollmentPrompt ? 'Make your next sign-in easier' : 'Add a passkey'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Set up a passkey to sign in without entering your password. Give it a name so you can recognize
                        it later.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Passkey name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                    />
                    {dialogError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEnrollment} disabled={submitting}>
                        {enrollmentPrompt ? 'Not now' : 'Cancel'}
                    </Button>
                    <Button variant="contained" onClick={() => void enroll()} disabled={!name.trim() || submitting}>
                        {submitting ? <CircularProgress size={20} /> : 'Set up passkey'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!renaming} onClose={() => !submitting && setRenaming(undefined)}>
                <DialogTitle>Rename passkey</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Passkey name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                        sx={{ mt: 1 }}
                    />
                    {dialogError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenaming(undefined)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={() => void rename()} disabled={!name.trim() || submitting}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleting} onClose={() => !submitting && setDeleting(undefined)}>
                <DialogTitle>Remove passkey?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You will no longer be able to sign in with “{deleting?.name}”.
                    </DialogContentText>
                    {dialogError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(undefined)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={() => void remove()} disabled={submitting}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!reauthenticationAction}
                onClose={() => !reauthenticating && setReauthenticationAction(undefined)}
                aria-labelledby="passkey-reauthentication-title"
            >
                <ReauthenticationForm
                    titleId="passkey-reauthentication-title"
                    reauthenticate={api.reauthenticate}
                    onBusyChange={setReauthenticating}
                    onCancel={() => setReauthenticationAction(undefined)}
                    onAuthenticated={async () => {
                        setReauthenticationAction(undefined)
                        if (reauthenticationAction === 'enroll') await enroll()
                        else await remove()
                    }}
                />
            </Dialog>
        </Paper>
    )
}
